import type { SoldCostsError, SoldCostsKey, SoldCostsMonth } from "./api";

export const SOLD_COSTS_FLOOR = "2025-01";

// C5 v2.11: the P&L tie view starts where Bojan asked for 100% (March 2026) and defaults to last month.
export const TIE_FLOOR = "2026-03";
export function tieMonth(value: unknown, thisMonth: string): string {
  const [y, m] = thisMonth.split("-").map(Number);
  const last = m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, "0")}`;
  const wanted = typeof value === "string" && /^\d{4}-\d{2}$/.test(value) ? value : last;
  return wanted < TIE_FLOOR ? TIE_FLOOR : wanted > last ? last : wanted;
}

export const COPY = {
  tieHeader: "Each company's Sold Costs for the month against the Cost of Goods Sold in the live Xero P&L, with every difference named. From March 2026.",
  header: "Sold-COGS per unit by SKU and month: the COGS-section costs (Cost of Product from the Unified sheet, raw-materials storage, EoM adjustments, QC), each per unit within the product. Per case for a case SKU. Open months may still change.",
  unavailable: "Sold Costs history is not available on this engine yet.",
  empty: "No Sold-COGS month has been computed yet. Sold Costs start with the first OFS month the layer has run.",
  detailEmpty: "No Sold-COGS month for this SKU in the window.",
  range: "Choose up to 36 months.",
  periodEmpty: (from: string, to: string, floor: string) => `Nothing to show: To (${to}) is before From (${from}). Sold Costs start ${floor}.`,
  floorNotice: (floor: string) => `Sold Costs start ${floor}; there is no Sold-COGS for earlier months.`,
  engineDown: "We can't reach the fulfilment costs engine, so nothing is shown. Try again shortly.",
  notConfigured: "The engine address is not set on the portal yet. Ask the portal owner.",
  checkField: "Check the highlighted field.",
  viewFee: "Fee per month",
  viewChange: "Change",
  viewPct: "% change",
  legendFee: "fee per unit as fed; a dash means no Sold-COGS value that month; red when up more than 0.01 in the row's currency against the previous month, green when down more than 0.01",
  legendChange: "amount up or down against the previous month; blank for the first month and after a gap; red when up more than 0.01 in the row's currency against the previous month, green when down more than 0.01",
  legendPct: "percent up or down against the previous month; blank for the first month and after a gap; red when up more than 0.01 in the row's currency against the previous month, green when down more than 0.01",
  sortHint: "Click a column heading to sort; click again to flip.",
} as const;

export function clampMonth(month: string, floor = SOLD_COSTS_FLOOR): string { return month < floor ? floor : month; }

export function monthsBetween(from: string, to: string): string[] {
  const [fy, fm] = from.split("-").map(Number);
  const [ty, tm] = to.split("-").map(Number);
  if (![fy, fm, ty, tm].every(Number.isFinite) || fy > ty || (fy === ty && fm > tm)) return [];
  const out: string[] = [];
  for (let y = fy, m = fm; y < ty || (y === ty && m <= tm); m === 12 ? (y++, m = 1) : m++) out.push(`${y}-${String(m).padStart(2, "0")}`);
  return out;
}

// C4 v1.6.10: Sold Costs opens on the last 8 months (Bojan, DEC-1596 PB-124, 3 Nov 2026; was 12 in C4 v1.6.8).
export const SOLD_COSTS_DEFAULT_MONTHS = 8;
export function defaultSoldCostsFrom(to: string, floor = SOLD_COSTS_FLOOR): string {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(to)) return floor;
  const [year, month] = to.split("-").map(Number);
  const start = new Date(Date.UTC(year, month - SOLD_COSTS_DEFAULT_MONTHS, 1));
  return clampMonth(`${start.getUTCFullYear()}-${String(start.getUTCMonth() + 1).padStart(2, "0")}`, floor);
}

export function sixMonthWindow(to: string, floor = SOLD_COSTS_FLOOR): { from: string; to: string } {
  const [year, month] = to.split("-").map(Number);
  const start = new Date(Date.UTC(year, month - 6, 1));
  return { from: clampMonth(`${start.getUTCFullYear()}-${String(start.getUTCMonth() + 1).padStart(2, "0")}`, floor), to };
}

export function sortAllowed(value: string, months: string[]): boolean {
  return ["sku", "product", "variant", "region", "channel", "location"].includes(value) || months.includes(value);
}

export function sortDirection(currentId: string, currentDirection: "asc" | "desc", nextId: string): "asc" | "desc" {
  return currentId === nextId && currentDirection === "asc" ? "desc" : "asc";
}

export function money(value: string | number | null | undefined, currency: string | null | undefined, dp = 2): string {
  if (value === null || value === undefined) return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  const safe = Math.abs(number) < 0.5 * 10 ** -dp ? 0 : number;
  const symbol = currency === "GBP" ? "£" : currency === "EUR" ? "€" : currency === "AED" ? "AED " : currency === "PLN" ? "zł" : currency === "SEK" ? "kr" : `${currency ?? ""} `;
  return `${symbol}${safe.toFixed(dp)}`;
}

export function signedMoney(value: string | null | undefined, currency: string | null | undefined): string {
  if (value === null || value === undefined) return "";
  const number = Number(value);
  if (!Number.isFinite(number)) return "";
  return `${number > 0 ? "+" : number < 0 ? "−" : ""}${money(Math.abs(number), currency)}`;
}

export function signedPct(value: string | null | undefined, dp = 1): string {
  if (value === null || value === undefined) return "";
  const number = Number(value);
  if (!Number.isFinite(number)) return "";
  return `${number > 0 ? "+" : number < 0 ? "−" : ""}${Math.abs(number).toFixed(dp)}%`;
}

export function moveClass(change: string | null | undefined): string | undefined {
  if (change === null || change === undefined) return undefined;
  const number = Number(change);
  if (!Number.isFinite(number)) return undefined;
  return number > 0.01 ? "text-ks-danger font-bold" : number < -0.01 ? "text-ks-pass font-bold" : undefined;
}

export function unitsLabel(key: Pick<SoldCostsKey, "row_kind" | "units_per_case">, month: Pick<SoldCostsMonth, "units">): string {
  const number = Number(month.units);
  const count = Number.isFinite(number) ? number.toLocaleString("en-GB", { maximumFractionDigits: 4 }) : month.units;
  if (key.row_kind === "case") return `${count} ${number === 1 ? "case" : "cases"} x${key.units_per_case}`;
  return `${count} units`;
}

export function messageFor(error: unknown): string {
  const value = error as SoldCostsError;
  if (!value || typeof value !== "object" || !("status" in value)) return COPY.engineDown;
  if (value.status === 404 || value.status === 405) return COPY.unavailable;
  if (value.status === 503 && value.code === "not_configured") return COPY.notConfigured;
  if (value.status === 422 && typeof value.body?.message === "string") return value.body.message;
  if (value.status === 422 && Array.isArray(value.body?.detail)) return value.body.detail[0]?.msg ?? COPY.checkField;
  return value.body?.message ?? COPY.engineDown;
}
